import React, { FC, useState } from 'react';
// Payment step disabled — the charge mutation/price query hooks and their supporting imports are commented out.
// import { useMutation } from 'react-relay';
import { T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { ButtonContainer } from '../../../shared/connectDialogStyle';
// Payment step disabled — Center/Spinner were only used by the price-summary Suspense fallback.
import { PrimaryButton } from '../../widgets/buttons';
import { Checkbox } from '../../widgets/checkbox';
import { Dialog, DialogWindow } from '../../widgets/dialog';
import { NotificationBanner } from '../../widgets/notificationBanner';
// Payment step disabled — showErrorNotification was only used by the charge error handlers.
// import { showErrorNotification } from '../../widgets/notificationToaster';
import { ZendeskAPI } from '../../widgets/toolbar/widgetZenDeskHelp';
import { ZTooltip } from '../../widgets/ztooltip';
// Payment step disabled — generated types for the commented-out charge/price operations.
// import { switchSalesChannelDialogCreateQuickChargeMutation } from './__generated__/switchSalesChannelDialogCreateQuickChargeMutation.graphql';
import { canSwitchSalesChannel } from './helpers';
// Payment step disabled — SwitchProgressDialog was only used by the "Processing Payment..." screen.
// import SwitchProgressDialog from './switch/switchProgressDialog';
import {
    BlockingProductsAlerts,
    CurrencyMismatchAlert,
    EstimatedProcessingTimeAlert,
    ImportantInformationAlert,
    LegacyPlanNotSupportedAlert,
    MigrationOnlyAlerts,
    ProductLimitReachedAlert,
    ProductLimitReachedSimpleAlert,
    ScalePlanRequiredAlert,
    SubscriptionRequirementAlert
} from './switch/switchSalesChannelDialogAlerts';
import { SellerPlanCheckInfo } from './switch/switchTypes';
import { SwitchWizardStep, SwitchWizardStepIndicator } from './switch/switchWizardSteps';

interface SwitchSalesChannelDialogProps {
    secondarySalesChannelID: string;
    secondaryEcommerceName: string;
    secondaryCurrencyCode?: string | null;
    masterSalesChannelID: string;
    masterEcommerceName: string;
    masterCurrencyCode?: string | null;
    planCheck?: SellerPlanCheckInfo;
    configuratorModelsCount?: number;
    hasPodProducts?: boolean;
    onClosed?: () => void;
    onPaymentSuccess?: (
        salesChannelID: string,
        masterSalesChannelID: string,
        isShopify: boolean,
        shopifyChargeID: string | null,
        hasAcknowledgedInfo: boolean
    ) => void;
}

const CustomDialog = styled(DialogWindow)`
    flex: 1;
    height: 100%;
    overflow-y: auto;
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const StepList = styled.ol`
    margin: 8px 0 0;
    padding-left: 20px;
    line-height: 1.8;

    li {
        padding-left: 4px;
    }
`;

const INLINE_BLOCK_STYLE = { display: 'inline-block' } as const;

/* Payment step disabled — the charge mutation, price query, and the price-summary UI are commented out.
 */
/** First matching reason wins, mirroring the original nested ternary. Kept as an if-chain so each T._ stays lazy. */
const getProceedTooltip = ({
    hasPodProducts,
    hasConfiguratorProducts,
    isProductLimitReached,
    hasAcknowledgedInfo
}: {
    hasPodProducts: boolean;
    hasConfiguratorProducts: boolean;
    isProductLimitReached: boolean;
    hasAcknowledgedInfo: boolean;
}): string => {
    if (hasPodProducts) return T._('You must remove all Print-on-Demand products from your account before proceeding.', 'Admin');
    if (hasConfiguratorProducts) return T._('You must remove all 3D Product Configurator products before proceeding.', 'Admin');
    if (isProductLimitReached) return T._('You must reduce your published products below the plan limit before proceeding.', 'Admin');
    if (!hasAcknowledgedInfo) return T._('Please confirm you have read all the information above', 'Admin');
    return '';
};

const ProceedWithPaymentButton: FC<{
    hasAcknowledgedInfo: boolean;
    isCreatingCharge: boolean;
    isPriceLoading: boolean;
    isProductLimitReached: boolean;
    hasConfiguratorProducts: boolean;
    hasPodProducts: boolean;
    onClick: () => void;
}> = ({ hasAcknowledgedInfo, isCreatingCharge, isPriceLoading, isProductLimitReached, hasConfiguratorProducts, hasPodProducts, onClick }) => (
    <ButtonContainer>
        <ZTooltip position='top' title={getProceedTooltip({ hasPodProducts, hasConfiguratorProducts, isProductLimitReached, hasAcknowledgedInfo })}>
            <span style={INLINE_BLOCK_STYLE}>
                <PrimaryButton
                    onClick={onClick}
                    disabled={!hasAcknowledgedInfo || isCreatingCharge || isPriceLoading || isProductLimitReached || hasConfiguratorProducts || hasPodProducts}
                >
                    {isCreatingCharge ? T._('Processing...', 'Admin') : T._('Proceed', 'Admin')}
                </PrimaryButton>
            </span>
        </ZTooltip>
    </ButtonContainer>
);

const ContactSupportButton: FC<{ onClick: () => void }> = ({ onClick }) => (
    <ButtonContainer>
        <PrimaryButton onClick={onClick}>{T._('Contact Support', 'Admin')}</PrimaryButton>
    </ButtonContainer>
);

const SwitchSalesChannelDialog: FC<SwitchSalesChannelDialogProps> = ({
    secondarySalesChannelID,
    secondaryEcommerceName,
    secondaryCurrencyCode,
    masterSalesChannelID,
    masterEcommerceName,
    masterCurrencyCode,
    planCheck,
    configuratorModelsCount,
    hasPodProducts,
    onClosed,
    onPaymentSuccess
}) => {
    // Payment step disabled — the charge mutation and its progress/price state are commented out.
    // const [createQuickCharge, isCreatingCharge] = useMutation<switchSalesChannelDialogCreateQuickChargeMutation>(createQuickChargeMutation);
    // const [isRedirecting, setIsRedirecting] = useState(false);
    const [hasAcknowledgedInfo, setHasAcknowledgedInfo] = useState(false);
    // const [switchPrice, setSwitchPrice] = useState<number | null>(null);

    const isShopify = masterEcommerceName === 'SHOPIFY' || secondaryEcommerceName === 'SHOPIFY';
    const bothAreShopify = masterEcommerceName === 'SHOPIFY' && secondaryEcommerceName === 'SHOPIFY';
    const requiresPaymentMethodSetup = (masterEcommerceName === 'API') !== (secondaryEcommerceName === 'API');
    const hasCurrencyMismatch = !!masterCurrencyCode && !!secondaryCurrencyCode && masterCurrencyCode !== secondaryCurrencyCode;

    // Payment step disabled — instead of creating a charge, the switch skips straight to the next step (Subscription)
    // for both Shopify and non-Shopify. onPaymentSuccess proceeds directly; see useSalesChannelSwitch.proceedToSubscriptionStep.
    const handleConfirm = () => {
        onClosed?.();
        const secondaryLocalID = toLocalID(secondarySalesChannelID);
        const masterLocalID = toLocalID(masterSalesChannelID);
        if (secondaryLocalID !== null && masterLocalID !== null) {
            onPaymentSuccess?.(secondaryLocalID.toString(), masterLocalID.toString(), isShopify, null, hasAcknowledgedInfo);
        }
    };

    /* Payment step disabled — original charge flow (Shopify redirect + non-Shopify direct charge) kept for reference.
    const handleConfirmWithPayment = () => {
        if (switchPrice === null) return;

        if (isShopify) {
            // Shopify charge flow - create the charge and redirect to Shopify
            const returnUrl = `${window.location.origin}/admin/sales-channels?paymentSuccess=true&salesChannelID=${toLocalID(
                secondarySalesChannelID
            )}&masterSalesChannelID=${toLocalID(masterSalesChannelID)}`;

            createQuickCharge({
                variables: {
                    input: {
                        isForTrial: false,
                        price: switchPrice,
                        description: `Sales Channel Switch - Master Integration Transfer`,
                        returnUrlFromApproval: returnUrl
                    }
                },
                onCompleted: (response) => {
                    // response.createQuickCharge contains the Shopify charge confirmation URL
                    if (response.createQuickCharge) {
                        setIsRedirecting(true);
                        window.location.href = response.createQuickCharge;
                    } else {
                        showErrorNotification({
                            message: T._('Failed to get payment URL. Please try again.', 'Admin')
                        });
                    }
                },
                onError: (error) => {
                    const message = (error as any).errors?.[0]?.message || error.message;
                    showErrorNotification({ message });
                }
            });
        } else {
            // Non-Shopify flow: execute payment directly via createQuickCharge
            createQuickCharge({
                variables: {
                    input: {
                        isForTrial: false,
                        price: switchPrice,
                        description: `Sales Channel Switch - Master Integration Transfer`,
                        returnUrlFromApproval: '' // Non-Shopify: no redirect needed
                    }
                },
                onCompleted: (_response) => {
                    onClosed?.();
                    const secondaryLocalID = toLocalID(secondarySalesChannelID);
                    const masterLocalID = toLocalID(masterSalesChannelID);
                    if (secondaryLocalID !== null && masterLocalID !== null) {
                        onPaymentSuccess?.(secondaryLocalID.toString(), masterLocalID.toString(), false, null, hasAcknowledgedInfo);
                    }
                },
                onError: (error) => {
                    const message = (error as any).errors?.[0]?.message || error.message;
                    showErrorNotification({ message });
                }
            });
        }
    };
    */

    const handleContactSupport = () => {
        // Close the dialog first
        onClosed?.();

        // Open Zendesk messenger
        ZendeskAPI('messenger', 'open');
    };

    const canSwitch = canSwitchSalesChannel(secondarySalesChannelID, secondaryEcommerceName, masterEcommerceName);
    const requiresScalePlan = planCheck?.maxProductsGrow != null && planCheck.currentProductCount > planCheck.maxProductsGrow;
    const hasConfiguratorProducts = (configuratorModelsCount ?? 0) > 0;
    const hasPodProductsBlocking = hasPodProducts === true;
    const isV1PlanBlocked = planCheck?.isV1Plan === true;
    const isNotV1RequiresScale = !isV1PlanBlocked && planCheck?.maxProductsGrow != null && planCheck.currentProductCount > planCheck.maxProductsGrow;
    const isNotV1ProductLimitReached = !isV1PlanBlocked && (planCheck?.isProductLimitReached ?? false);
    const isBlocked = hasConfiguratorProducts || hasPodProductsBlocking;

    /* Payment step disabled — the "Processing Payment..." progress screen is no longer reachable.
    if (isCreatingCharge || isRedirecting) {
        return (
            <SwitchProgressDialog
                showCloseButton={false}
                step={SwitchWizardStep.Payment}
                title={T._('Processing Payment...', 'Admin')}
                note={isShopify ? T._('You will be redirected to the payment page shortly.', 'Admin') : ''}
                secondaryNote={T._('Please do not close this window.', 'Admin')}
            />
        );
    }
    */

    // The seller cannot switch automatically — only a manual migration is possible.
    if (!canSwitch) {
        return (
            <Dialog
                windowDecorator={isBlocked || isV1PlanBlocked ? undefined : CustomDialog}
                title={T._('Switch Master Integration Sales Channel', 'Admin')}
                titleHtml={<SwitchWizardStepIndicator step={SwitchWizardStep.Review} />}
            >
                <DialogContent>
                    <MigrationOnlyAlerts />
                    <ContactSupportButton onClick={handleContactSupport} />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            windowDecorator={isBlocked || isV1PlanBlocked ? undefined : CustomDialog}
            title={T._('Switch Master Integration Sales Channel', 'Admin')}
            titleHtml={<SwitchWizardStepIndicator step={SwitchWizardStep.Review} />}
        >
            <DialogContent>
                {isV1PlanBlocked ? (
                    // Legacy V1 plans cannot switch at all.
                    <>
                        <LegacyPlanNotSupportedAlert />
                        <ContactSupportButton onClick={handleContactSupport} />
                    </>
                ) : isBlocked ? (
                    // POD or 3D Configurator products must be removed first. Note this branch never renders
                    // SalesChannelPriceSummary, so switchPrice stays null and the button stays disabled.
                    <>
                        <BlockingProductsAlerts hasPodProducts={hasPodProductsBlocking} hasConfiguratorProducts={hasConfiguratorProducts} />
                        <ProceedWithPaymentButton
                            hasAcknowledgedInfo={hasAcknowledgedInfo}
                            isCreatingCharge={false}
                            isPriceLoading={false}
                            isProductLimitReached={planCheck?.isProductLimitReached ?? false}
                            hasConfiguratorProducts={hasConfiguratorProducts}
                            hasPodProducts={hasPodProductsBlocking}
                            onClick={handleConfirm}
                        />
                    </>
                ) : (
                    // The switch can proceed. The plan-check alerts below are not mutually exclusive.
                    <>
                        {isNotV1RequiresScale && <ScalePlanRequiredAlert />}
                        {isNotV1ProductLimitReached && planCheck && <ProductLimitReachedAlert planCheck={planCheck} />}
                        {!isNotV1RequiresScale && !isNotV1ProductLimitReached && planCheck?.isProductLimitReached && (
                            <ProductLimitReachedSimpleAlert planCheck={planCheck} />
                        )}

                        <EstimatedProcessingTimeAlert />

                        <NotificationBanner
                            type='INFO'
                            title={T._('Process Overview', 'Admin')}
                            content={
                                <StepList>
                                    {requiresPaymentMethodSetup && (
                                        <li>
                                            <strong>{T._('Payment method setup', 'Admin')}</strong>
                                            {' — '}
                                            {T._(
                                                'Because you are switching between an API and a non-API integration, you will need to enter your payment method again before switching products.',
                                                'Admin'
                                            )}
                                        </li>
                                    )}
                                    {bothAreShopify && (
                                        <li>
                                            <strong>{T._('New subscription', 'Admin')}</strong>
                                            {' — '}
                                            {requiresScalePlan ? T._('SCALE plan or higher required', 'Admin') : T._('GROW plan or higher required', 'Admin')}
                                        </li>
                                    )}
                                    <li>{T._('Product switch', 'Admin')}</li>
                                </StepList>
                            }
                        />

                        {/* Payment step disabled — the "Payment & Pricing" summary is no longer shown.
                        <NotificationBanner
                            type='INFO'
                            title={T._('Payment & Pricing', 'Admin')}
                            content={
                                <Suspense
                                    fallback={
                                        <Center>
                                            <Spinner />
                                        </Center>
                                    }
                                >
                                    <FinancialPreview salesChannelID={secondarySalesChannelID} />
                                </Suspense>
                            }
                        />
                        */}

                        {hasCurrencyMismatch && <CurrencyMismatchAlert secondaryCurrencyCode={secondaryCurrencyCode} masterCurrencyCode={masterCurrencyCode} />}

                        <ImportantInformationAlert />

                        {bothAreShopify && (
                            <SubscriptionRequirementAlert requiresScalePlan={requiresScalePlan} currentProductCount={planCheck?.currentProductCount} />
                        )}

                        <Checkbox
                            label={T._('I confirm I have read and understood all the information above and I am ready to proceed.', 'Admin')}
                            checked={hasAcknowledgedInfo}
                            onChange={(value) => setHasAcknowledgedInfo(value as boolean)}
                        />

                        <ProceedWithPaymentButton
                            hasAcknowledgedInfo={hasAcknowledgedInfo}
                            isCreatingCharge={false}
                            isPriceLoading={false}
                            isProductLimitReached={planCheck?.isProductLimitReached ?? false}
                            hasConfiguratorProducts={false}
                            hasPodProducts={false}
                            onClick={handleConfirm}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SwitchSalesChannelDialog;
